import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { CheckCircle, Clock, Dumbbell } from "lucide-react";
import { GymHeader } from "@/components/GymHeader";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function GymRegister() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  const utils = trpc.useUtils();

  const { data: config } = trpc.gym.getConfig.useQuery();
  const { data: services } = trpc.gym.services.list.useQuery({});
  const { data: profile, isLoading: profileLoading } = trpc.gym.member.profile.useQuery(
    undefined,
    { enabled: !!user }
  );

  // No longer needed - registration happens on complete page

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12 text-center">
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle>Anmeldung erforderlich</CardTitle>
              <CardDescription>
                Bitte melden Sie sich an, um Gym-Mitglied zu werden
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => window.location.href = getLoginUrl("/gym/register/complete")}>
                Jetzt anmelden
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const gymStatus = profile?.gymStatus;
  const isGymMember = profile?.roles?.includes("gym_member");

  // Already a member
  if (isGymMember && gymStatus === "approved") {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <CardTitle>Sie sind bereits Mitglied!</CardTitle>
              <CardDescription>
                Sie können jetzt Trainingsslots buchen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button className="w-full" onClick={() => setLocation("/gym/calendar")}>
                Zum Kalender
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setLocation("/gym/bookings")}>
                Meine Buchungen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Pending approval
  if (gymStatus === "pending") {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <CardTitle>Anfrage wird bearbeitet</CardTitle>
              <CardDescription>
                Ihre Mitgliedsanfrage wird derzeit geprüft. Sie erhalten eine Benachrichtigung, sobald Sie freigeschaltet wurden.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" onClick={() => setLocation("/gym")}>
                Zurück zur Übersicht
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Rejected
  if (gymStatus === "rejected") {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12">
          <Card className="max-w-md mx-auto border-destructive">
            <CardHeader className="text-center">
              <CardTitle className="text-destructive">Anfrage abgelehnt</CardTitle>
              <CardDescription>
                Ihre Mitgliedsanfrage wurde leider abgelehnt. Bitte kontaktieren Sie uns für weitere Informationen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" onClick={() => setLocation("/gym")}>
                Zurück zur Übersicht
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen bg-background">
      <GymHeader />
      
      <div className="container py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Dumbbell className="h-16 w-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-bold">Gym-Mitglied werden</h1>
            <p className="text-muted-foreground mt-2">
              Registrieren Sie sich für unser Fitnessstudio und buchen Sie Trainingsslots
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ihre Vorteile als Mitglied</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Flexible Slot-Buchung über unseren Online-Kalender</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Bis zu {config?.gym?.maxBookingsPerWeek ?? 5} Buchungen pro Woche</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Einfacher Self-Service Check-in</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Zugang zu allen Trainingsbereichen</span>
                </li>
              </ul>

              {services && services.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Verfügbare Trainingsbereiche:</h4>
                  <div className="flex flex-wrap gap-2">
                    {services.filter(s => s.isActive).map(service => (
                      <div 
                        key={service.id}
                        className="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                        style={{ 
                          backgroundColor: `${service.color}20`,
                          color: service.color
                        }}
                      >
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: service.color }}
                        />
                        {service.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Mitgliedschaft beantragen</CardTitle>
              <CardDescription>
                Ihre Anfrage wird von unserem Team geprüft. Sie erhalten eine Benachrichtigung, sobald Sie freigeschaltet wurden.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm">
                    <strong>Angemeldet als:</strong> {user.name || user.email}
                  </p>
                  {user.email && (
                    <p className="text-sm text-muted-foreground">
                      {user.email}
                    </p>
                  )}
                </div>
                
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => setLocation("/gym/register/complete")}
                >
                  Weiter
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  Mit der Beantragung stimmen Sie unseren Nutzungsbedingungen zu.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
